import React, { useState, useEffect } from "react";
import { Button, Table, Modal, Form, Select, InputNumber, Space, Popconfirm, Input } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import plazaAbbreviations from "./tollData";
import { useToast } from "../../../../contexts/ToastContext";
import apiClient from "../../../../helpers/apiClient";
import customizeRequiredMark from "../../../../components/custom_antd/CustomizeRequiredMark";
import SettingsPageLayout from "../SettingsPageLayout";

const TollUpcharge = ({ companyData, initialLoading, fetchCompanyData }) => {
    const { t } = useTranslation("settings");
    const toast = useToast();
    const [form] = Form.useForm();

    // State management
    const [tollPlazas, setTollPlazas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalType, setModalType] = useState(""); // "add" | "edit"
    const [, setEditingRecord] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    // Load toll plazas from company data
    useEffect(() => {
        if (companyData?.tollUpCharge) {
            setTollPlazas(companyData.tollUpCharge);
        }
    }, [companyData]);

    // Get plaza options for dropdown (existing plazas + custom option)
    const plazaOptions = [
        ...plazaAbbreviations.map((plaza) => ({
            value: plaza.abbreviation,
            label: `${plaza.plaza_name} (${plaza.abbreviation})`,
            searchLabel: `${plaza.plaza_name} ${plaza.abbreviation}`,
            isCustom: false,
        })),
        {
            value: "custom",
            label: "Custom Toll Plaza",
            searchLabel: "Custom Toll Plaza",
            isCustom: true,
        },
    ];

    // Check if plaza is already added
    const isPlazaAdded = (abbreviation) => tollPlazas.some((plaza) => plaza.abbreviation === abbreviation);

    // Filter available plazas for add modal
    const availablePlazaOptions = plazaOptions.filter((option) => option.isCustom || !isPlazaAdded(option.value));

    // Handle add toll plaza
    const handleAdd = () => {
        setModalType("add");
        setEditingRecord(null);
        form.resetFields();
        setModalVisible(true);
    };

    // Handle edit toll plaza
    const handleEdit = (record) => {
        setModalType("edit");
        setEditingRecord(record);

        // Check if it's a custom plaza (not in predefined list)
        const isCustomPlaza = !plazaAbbreviations.find((p) => p.abbreviation === record.abbreviation);

        form.setFieldsValue({
            plaza: isCustomPlaza ? "custom" : record.abbreviation,
            customPlazaName: isCustomPlaza ? record.tollPlaza : "",
            customAbbreviation: isCustomPlaza ? record.abbreviation : "",
            charge: record.charge,
        });
        setModalVisible(true);
    };

    // Handle delete toll plaza
    const handleDelete = async (record) => {
        try {
            setLoading(true);

            const response = await apiClient.delete(`/api/toll-up-charge/${record.abbreviation}`);

            if (response.data.success) {
                const updatedTollPlazas = tollPlazas.filter((plaza) => plaza.abbreviation !== record.abbreviation);
                setTollPlazas(updatedTollPlazas);

                toast.success({
                    message: t("tollUpcharge.deleteSuccess"),
                    description: t("tollUpcharge.plazaDeletedSuccessfully"),
                });

                if (fetchCompanyData) {
                    await fetchCompanyData();
                }
            }
        } catch (error) {
            toast.error({
                message: t("tollUpcharge.deleteError"),
                description: error.response?.data?.message || t("tollUpcharge.couldNotDeletePlaza"),
            });
        } finally {
            setLoading(false);
        }
    };

    // Handle form submission
    const handleSubmit = async (values) => {
        try {
            setSubmitting(true);

            let tollPlazaData;

            if (values.plaza === "custom") {
                // Custom plaza
                tollPlazaData = {
                    tollPlaza: values.customPlazaName,
                    abbreviation: values.customAbbreviation,
                    charge: values.charge,
                };
            } else {
                // Predefined plaza
                const selectedPlaza = plazaAbbreviations.find((plaza) => plaza.abbreviation === values.plaza);
                tollPlazaData = {
                    tollPlaza: selectedPlaza.plaza_name,
                    abbreviation: values.plaza,
                    charge: values.charge,
                };
            }

            let response;

            if (modalType === "add") {
                response = await apiClient.post("/api/toll-up-charge", tollPlazaData);
            } else {
                response = await apiClient.patch(`/api/toll-up-charge`, tollPlazaData);
            }

            if (response.data.success) {
                toast.success({
                    message: modalType === "add" ? t("tollUpcharge.addSuccess") : t("tollUpcharge.updateSuccess"),
                    description:
                        modalType === "add"
                            ? t("tollUpcharge.plazaAddedSuccessfully")
                            : t("tollUpcharge.plazaUpdatedSuccessfully"),
                });

                setModalVisible(false);
                form.resetFields();

                if (fetchCompanyData) {
                    await fetchCompanyData();
                }
            }
        } catch (error) {
            toast.error({
                message: modalType === "add" ? t("tollUpcharge.addError") : t("tollUpcharge.updateError"),
                description:
                    error.response?.data?.message ||
                    (modalType === "add" ? t("tollUpcharge.couldNotAddPlaza") : t("tollUpcharge.couldNotUpdatePlaza")),
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Watch plaza selection to show/hide custom fields
    const selectedPlaza = Form.useWatch("plaza", form);
    const isCustomSelected = selectedPlaza === "custom";

    // Table columns
    const columns = [
        {
            title: t("tollUpcharge.plazaName"),
            dataIndex: "tollPlaza",
            key: "tollPlaza",
            render: (text, record) => (
                <div>
                    <div className="font-medium">{text}</div>
                    <div className="text-sm text-gray-500">({record.abbreviation})</div>
                </div>
            ),
        },
        {
            title: t("tollUpcharge.charge"),
            dataIndex: "charge",
            key: "charge",
            render: (charge) => `$${charge.toFixed(2)}`,
            sorter: (a, b) => a.charge - b.charge,
        },
        {
            title: t("actions"),
            key: "actions",
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        className="text-blue-600 hover:text-blue-800"
                    />
                    <Popconfirm
                        title={t("tollUpcharge.confirmDelete")}
                        description={t("tollUpcharge.confirmDeleteDescription")}
                        onConfirm={() => handleDelete(record)}
                        okText={t("yes")}
                        cancelText={t("no")}
                        okButtonProps={{ danger: true }}>
                        <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            danger
                            className="text-red-600 hover:text-red-800"
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <SettingsPageLayout
            title={t("tollUpcharge.title")}
            description={t("tollUpcharge.description")}
            actions={
                <Button type="primary" shape="round" icon={<PlusOutlined />} onClick={handleAdd}>
                    {t("tollUpcharge.addTollPlaza")}
                </Button>
            }>
            {/* Statistics */}
            <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="bg-white p-4 rounded-lg border">
                    <div className="text-2xl font-bold text-blue-600">{tollPlazas.length}</div>
                    <div className="text-sm text-gray-600">{t("tollUpcharge.totalPlazas")}</div>
                </div>
                <div className="bg-white p-4 rounded-lg border">
                    <div className="text-2xl font-bold text-green-600">{plazaAbbreviations.length}</div>
                    <div className="text-sm text-gray-600">{t("tollUpcharge.availablePlazas")}</div>
                </div>
            </div>

            {/* Table */}
            <div className="flex-1 bg-white rounded-lg border">
                <Table
                    className="sc-table-frame"
                    columns={columns}
                    dataSource={tollPlazas}
                    rowKey="abbreviation"
                    loading={initialLoading || loading}
                    pagination={{
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} ${t("of")} ${total} ${t("tollUpcharge.plazas")}`,
                    }}
                    locale={{
                        emptyText: (
                            <div className="py-8">
                                <div className="text-gray-400 text-lg mb-2">{t("tollUpcharge.noPlazasConfigured")}</div>
                                <div className="text-gray-500 text-sm">{t("tollUpcharge.addFirstPlaza")}</div>
                            </div>
                        ),
                    }}
                />
            </div>

            {/* Modal */}
            <Modal
                title={modalType === "add" ? t("tollUpcharge.addTollPlaza") : t("tollUpcharge.editTollPlaza")}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                }}
                footer={null}
                width={500}
                destroyOnClose>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    className="mt-4"
                    requiredMark={customizeRequiredMark}>
                    <Form.Item
                        name="plaza"
                        label={t("tollUpcharge.selectPlaza")}
                        rules={[
                            {
                                required: true,
                                message: t("tollUpcharge.pleaseSelectPlaza"),
                            },
                        ]}>
                        <Select
                            showSearch
                            placeholder={t("tollUpcharge.searchPlaza")}
                            options={modalType === "add" ? availablePlazaOptions : plazaOptions}
                            filterOption={(input, option) =>
                                (option?.searchLabel ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                            disabled={modalType === "edit"}
                        />
                    </Form.Item>

                    {/* Custom Plaza Fields */}
                    {isCustomSelected && (
                        <>
                            <Form.Item
                                name="customPlazaName"
                                label={t("tollUpcharge.customPlazaName")}
                                rules={[
                                    {
                                        required: true,
                                        message: t("tollUpcharge.pleaseEnterPlazaName"),
                                    },
                                ]}>
                                <Input placeholder={t("tollUpcharge.enterPlazaName")} />
                            </Form.Item>

                            <Form.Item
                                name="customAbbreviation"
                                label={t("tollUpcharge.abbreviation")}
                                rules={[
                                    {
                                        required: true,
                                        message: t("tollUpcharge.pleaseEnterAbbreviation"),
                                    },
                                    {
                                        validator: async (_, value) => {
                                            if (value && modalType === "add" && isPlazaAdded(value)) {
                                                throw new Error(t("tollUpcharge.abbreviationAlreadyExists"));
                                            }
                                        },
                                    },
                                ]}>
                                <Input
                                    placeholder={t("tollUpcharge.enterAbbreviation")}
                                    maxLength={10}
                                    disabled={modalType === "edit"}
                                />
                            </Form.Item>
                        </>
                    )}

                    <Form.Item
                        name="charge"
                        label={t("tollUpcharge.chargeAmount")}
                        rules={[
                            {
                                required: true,
                                message: t("tollUpcharge.pleaseEnterCharge"),
                            },
                            {
                                type: "number",
                                min: 0,
                                message: t("tollUpcharge.chargeMinimum"),
                            },
                        ]}>
                        <InputNumber
                            style={{ width: "100%" }}
                            placeholder="0.00"
                            min={0}
                            step={0.01}
                            precision={2}
                            formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                            parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                        />
                    </Form.Item>

                    <Form.Item className="mb-0 flex justify-end gap-2">
                        <Space>
                            <Button
                                onClick={() => {
                                    setModalVisible(false);
                                    form.resetFields();
                                }}
                                disabled={submitting}>
                                {t("cancel")}
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={submitting}
                                className="bg-blue-600 hover:bg-blue-700">
                                {submitting
                                    ? t("saving")
                                    : modalType === "add"
                                      ? t("tollUpcharge.addPlaza")
                                      : t("tollUpcharge.updatePlaza")}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </SettingsPageLayout>
    );
};

TollUpcharge.propTypes = {
    companyData: PropTypes.object,
    initialLoading: PropTypes.bool,
    fetchCompanyData: PropTypes.func,
};

export default TollUpcharge;
